import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const TERMS_CONTENT = `Bienvenida a Wellaging Argentino. Al acceder y utilizar este sitio web y/o adquirir nuestros productos digitales, aceptás los siguientes términos y condiciones.

1. PRODUCTO DIGITAL
"Wellaging Argentino" es un producto digital descargable (ebook + bonuses) destinado a uso personal e individual. La compra otorga una licencia de uso personal y no transferible.

2. PROPIEDAD INTELECTUAL
Todo el contenido (textos, imágenes, diseño, método) es propiedad de Wellaging Argentino. Está prohibida su redistribución, reventa, copia o reproducción total o parcial sin autorización expresa.

3. CARÁCTER DEL CONTENIDO
La información provista es de carácter educativo e informativo. No reemplaza consultas, diagnósticos ni tratamientos médicos profesionales. Ante cualquier condición médica, consultá con un profesional de la salud.

4. RESULTADOS
Los resultados pueden variar según cada persona, su compromiso con el método y sus condiciones particulares. No garantizamos resultados específicos.

5. PAGO Y ACCESO
El pago se procesa de forma segura a través de MercadoPago. Una vez confirmado el pago, recibirás los archivos por email de forma inmediata.

6. JURISDICCIÓN
Estos términos se rigen por las leyes de la República Argentina.

Última actualización: 2026.`;
const PRIVACY_CONTENT = `POLÍTICA DE PRIVACIDAD — Wellaging Argentino

1. RESPONSABLE DEL TRATAMIENTO
Wellaging Argentino es responsable del tratamiento de tus datos personales recolectados a través de este sitio.

2. DATOS QUE RECOPILAMOS
- Nombre y apellido
- Dirección de email
- Datos de facturación necesarios para procesar tu compra
- Información técnica básica (cookies, IP, navegador) para mejorar la experiencia

3. FINALIDAD DEL TRATAMIENTO
Tus datos se utilizan exclusivamente para:
- Procesar tu compra y entregarte el producto digital
- Enviarte el contenido adquirido y soporte relacionado
- Comunicaciones relevantes sobre tu compra
- Cumplir con obligaciones legales

4. PROTECCIÓN DE DATOS
Implementamos medidas técnicas y organizativas para proteger tu información (encriptación SSL, procesamiento seguro a través de MercadoPago).

5. NO COMPARTIMOS TUS DATOS
No vendemos ni cedemos tus datos personales a terceros. Solo se comparten con proveedores estrictamente necesarios para procesar tu compra (MercadoPago).

6. TUS DERECHOS
Podés solicitar acceso, rectificación o eliminación de tus datos en cualquier momento, escribiéndonos por los canales habilitados.

7. COOKIES
Utilizamos cookies técnicas y analíticas. Podés configurar tu navegador para rechazarlas.

Última actualización: 2026.`;
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  content: string;
}
function LegalModal({
  open,
  onClose,
  title,
  content
}: ModalProps) {
  return <AnimatePresence data-id="element-269">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-charcoal/80 backdrop-blur-sm" onClick={onClose} data-id="element-270">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="bg-cream-light rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-gold/20" onClick={e => e.stopPropagation()} data-id="element-271">
            <div className="flex items-center justify-between p-6 md:p-8 border-b border-gold/20" data-id="element-272">
              <h3 className="text-2xl font-serif text-charcoal" data-id="element-273">{title}</h3>
              <button onClick={onClose} aria-label="Cerrar" className="w-10 h-10 rounded-full bg-cream-dark hover:bg-gold/20 flex items-center justify-center transition-colors text-charcoal" data-id="element-274">
                <X className="w-5 h-5" data-id="element-275" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto text-charcoal-light font-light text-sm leading-relaxed whitespace-pre-line" data-id="element-276">
              {content}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [openModal, setOpenModal] = useState<'terms' | 'privacy' | null>(null);
  return <>
      <footer className="bg-charcoal text-white/50 py-16 font-light text-sm" data-id="element-277">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 text-center md:text-left" data-id="element-278">
          <div className="space-y-4" data-id="element-279">
            <h4 className="text-cream-light font-serif text-2xl mb-6" data-id="element-280">
              Wellaging Argentino
            </h4>
            <p className="text-xs uppercase tracking-widest text-gold" data-id="element-281">
              Compra 100% Segura
            </p>
            <p data-id="element-282">
              Procesado por MercadoPago
              <br data-id="element-283" />
              Datos encriptados SSL
            </p>
          </div>

          <div className="space-y-4 md:text-right" data-id="element-284">
            <h4 className="text-cream-light font-medium mb-6 uppercase tracking-widest text-xs" data-id="element-285">
              Legal
            </h4>
            <ul className="space-y-3" data-id="element-286">
              <li data-id="element-287">
                <button onClick={() => setOpenModal('terms')} className="hover:text-gold transition-colors" data-id="element-288">
                  Términos y Condiciones
                </button>
              </li>
              <li data-id="element-289">
                <button onClick={() => setOpenModal('privacy')} className="hover:text-gold transition-colors" data-id="element-290">
                  Política de Privacidad
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 mt-16 pt-8 border-t border-white/10 text-center text-xs" data-id="element-291">
          <p className="mb-4" data-id="element-292">🇦🇷 Hecho en Argentina, Para Argentinas</p>
          <p data-id="element-293">© 2026 Wellaging Argentino. Todos los derechos reservados.</p>
        </div>
      </footer>

      <LegalModal open={openModal === 'terms'} onClose={() => setOpenModal(null)} title="Términos y Condiciones" content={TERMS_CONTENT} data-id="element-294" />
      <LegalModal open={openModal === 'privacy'} onClose={() => setOpenModal(null)} title="Política de Privacidad" content={PRIVACY_CONTENT} data-id="element-295" />
    </>;
}